import { Images } from '@/assets/images';
import { REGEX } from '@/constant';
import {
	cn,
	convertProductionTypeCodeToName,
	dayjs,
	diffDate,
	formatDate,
	insertObjectIf,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';

import { Dayjs } from 'dayjs';
import { range } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import * as Yup from 'yup';

import { CheckboxType, FormConfig } from '../define-document-write.type';

const HOLIDAYS = ['1time/week', '4times/month', 'etc'];
const TIME_PER_WAGE = ['Monthly', 'Hourly', 'Daily', 'Weekly'];
const AGRICULTURE_TYPE = ['Agriculture', 'Fishery'];
const LABOR_CONTRACT = ['Newcomer', 'Employee'];
const BONUS_OR_EXTRA_PAGE = ['0', '1'];
const PAYMENT_DATE = ['month', 'next month'];
const PAYMENT_WEEK = ['week', 'next week'];
const PROVIDED_STATUS = ['1', '0'];
const MEAL_PROVIDED = ['0'];
const ACCOMMODATION_TYPES = [
	'house',
	'lodging facilities',
	'prefabricated accommodation',
	'workplace buildings',
	'other lodging facilities',
];

const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];

const initializeValue = {
	enterpriseName: '',
	phoneNumber: '',
	enterpriseLocation: '',
	employerBirthday: '',
	identificationNumber: '',

	// Show DB
	applicantName: '',
	birthDate: '',
	address: '',

	// 관리번호
	year: '',
	district: '',
	managementNumber: '',
	agricultureType: 'Agriculture',

	// 1: Term of labor contract
	laborContract: [] as string[],
	newOrReenter: '8',

	reEnteringStartDate: '',
	reEnteringEndDate: '',
	reEnteringMonth: '8',

	changeWorkplaceStartDate: '',
	changeWorkplaceEndDate: '',
	changeWorkplaceMonth: '8',

	// 2: Place of employment
	// placeOfEmployment: '',
	employAddress: '',

	// 3: Description of work
	typeOfBusiness: '농업',
	// industry: '',
	businessDescr: '',
	jobDescr: '',

	// 4: Working hours
	workingDayFrom: '월',
	workingDayTo: '토',
	workingHourFrom: '08:00',
	workingHourTo: '17:00',
	workingHourPerMonth: '209',
	workingHourWeather: '',

	// 5: Recess hours
	recessHoursTimes: '1',
	recessHoursTotalHours: '1',
	recessHoursTotalMinutes: '',

	// 6: Holidays
	holidays: HOLIDAYS[1],
	holidaysEtc: '',

	// 7: Payment
	wage: '2156880',
	timePerWage: TIME_PER_WAGE[0],
	weeklyHolidayAllow: '1', // uncheck
	bonusOrExtraPay: '1', // uncheck
	bonusPay: '',
	extraPay: '',
	overTimePay: '10320',
	// 8: Payment date
	paymentDatePerMonth: '10',
	paymentDatePerWeek: '',
	// paymentDatePerWeekEn: '',
	paymentDate: 'next month',
	paymentWeek: '',
	calculateFrom: '월초',
	calculateTo: '월말',
	// calculatePaid: '10',

	// 9: Payment methods
	paymentMethod: '1', // uncheck

	// 10: Accommodations and Meals
	provideAccommondationStatus: '1',
	provideAccommondationStatusNot: '',
	otherLodging: '',
	provideAccommondationOther: '',

	provideAccommondationType: [] as string[],

	accommodationCost: '323000',

	provideMeals: '1', // check
	mealsType: [MEAL_TYPES[1]] as string[],
	mealsCost: '107000',
	mealsProviderOther: '',

	// 11: date-name-signature
	standardEmplCtrRegistDate: '',
	standardEmployer: '',
	standardEmployerSignature: '',
	standardEmplCtrEmployee: '',
	standardEmplCtrSignature: '',

	// 14: newOrReenter
	entryDate: '',
	departureDate: '',
};

type IValues = typeof initializeValue;

const disabledDateStartDate = (current: Dayjs, date: string) => {
	return (
		!!date &&
		current &&
		current > dayjs(date, SUPPORT_FORMAT_DATE_LIST).endOf('day')
	);
};

const disabledDateEndDate = (current: Dayjs, date: string) => {
	return !!date && current && current < dayjs(date, SUPPORT_FORMAT_DATE_LIST);
};

const disabledRangeTimeFrom = (date: string) => {
	const disabledHours = () => {
		if (!date) return [];
		return range(0, 60).splice(dayjs(date, 'HH:mm').hour() + 1);
	};

	const disabledMinutes = (hour: any) => {
		if (hour === dayjs(date, 'HH:mm').hour()) {
			return range(0, 60).splice(dayjs(date, 'HH:mm').minute() + 1);
		}

		return [];
	};

	return {
		disabledHours,
		disabledMinutes,
	};
};

const disabledRangeTimeTo = (date: string) => {
	const disabledHours = () => {
		if (!date) return [];
		return range(0, 60).splice(0, dayjs(date, 'HH:mm').hour());
	};

	const disabledMinutes = (hour: any) => {
		if (hour === dayjs(date, 'HH:mm').hour()) {
			return range(0, 60).splice(0, dayjs(date, 'HH:mm').minute());
		}

		return [];
	};

	return {
		disabledHours,
		disabledMinutes,
	};
};

export const STANDARD_LABOR_CONTRACT_CONFIG: FormConfig<IValues> = {
	initializeValue,
	// Two-way relationship between the "month count" and the "start/end date" pair:
	// - Change a date  -> recompute the month count = month diff of the two dates.
	// - Change the month count -> recompute the end date = start date + month count.
	derivedFields: [
		// re-entering: dates -> month count
		{
			when: ['reEnteringStartDate', 'reEnteringEndDate'],
			set: 'reEnteringMonth',
			compute: (data) =>
				data.reEnteringStartDate && data.reEnteringEndDate
					? String(
						diffDate(
							data.reEnteringEndDate,
							data.reEnteringStartDate,
							'month'
						)
					)
					: '',
		},
		// re-entering: month count -> end date
		{
			when: ['reEnteringMonth'],
			set: 'reEnteringEndDate',
			compute: (data) => {
				const months = Number(data.reEnteringMonth);
				return data.reEnteringStartDate && !Number.isNaN(months) && months > 0
					? dayjs(formatDate(data.reEnteringStartDate))
						.add(months, 'month')
						.format('YYYY-MM-DD')
					: data.reEnteringEndDate || '';
			},
		},
		// change-workplace: dates -> month count
		{
			when: ['changeWorkplaceStartDate', 'changeWorkplaceEndDate'],
			set: 'changeWorkplaceMonth',
			compute: (data) =>
				data.changeWorkplaceStartDate && data.changeWorkplaceEndDate
					? String(
						diffDate(
							data.changeWorkplaceEndDate,
							data.changeWorkplaceStartDate,
							'month'
						)
					)
					: '',
		},
		// change-workplace: month count -> end date
		{
			when: ['changeWorkplaceMonth'],
			set: 'changeWorkplaceEndDate',
			compute: (data) => {
				const months = Number(data.changeWorkplaceMonth);
				return data.changeWorkplaceStartDate &&
					!Number.isNaN(months) &&
					months > 0
					? dayjs(formatDate(data.changeWorkplaceStartDate))
						.add(months, 'month')
						.format('YYYY-MM-DD')
					: data.changeWorkplaceEndDate || '';
			},
		},
	],
	mapPropToValues({ refTableName, franchiseData, applicantData }) {
		return {
			...insertObjectIf(isNotNilOrEmpty(applicantData), {
				applicantName: applicantData?.fullName || '',
				birthDate: applicantData?.birthDate,
				address: applicantData?.hometownAddr,
				standardEmplCtrEmployee: applicantData?.fullName || '',

				entryDate: applicantData?.entryDate || '',
				departureDate: applicantData?.departureDate || '',
			}),
			...insertObjectIf(isNotNilOrEmpty(franchiseData), {
				enterpriseName: franchiseData?.name || '',
				phoneNumber: franchiseData?.employerPhone || '',
				enterpriseLocation: `${franchiseData?.address || ''}`,
				employerBirthday: franchiseData?.employerBirthDate
					? formatDate(franchiseData?.employerBirthDate)
					: '',
				identificationNumber: franchiseData?.businessRegistNo
					? franchiseData?.businessRegistNo
					: '',

				// placeOfEmployment: franchiseData?.address || '',

				// industry:
				// 	convertFranchiseTypeDetailCodeToName(franchiseData?.typeDetail) || '',
				businessDescr:
					convertProductionTypeCodeToName(franchiseData?.productionType) || '',
				jobDescr: franchiseData?.productionTypeDetail || '',

				workingHourFrom: dayjs(franchiseData?.workEndTime, 'HH:mm').isBefore(
					dayjs(franchiseData?.workStartTime || '08:00', 'HH:mm')
				)
					? ''
					: franchiseData?.workStartTime || '08:00',
				workingHourTo: dayjs(franchiseData?.workStartTime, 'HH:mm').isAfter(
					dayjs(franchiseData?.workEndTime || '17:00', 'HH:mm')
				)
					? ''
					: franchiseData?.workEndTime || '17:00',

				workingHourPerMonth: String(
					franchiseData?.monthlyWorkingHours || '209'
				),

				recessHoursTimes: String(franchiseData?.dailyBreaksNumber || '1'),
				recessHoursTotalHours: String(franchiseData?.dailyBreaksHour || '1'),
				recessHoursTotalMinutes: String(franchiseData?.dailyBreaksMin || ''),
				standardEmployer: franchiseData?.employerName || '',
			}),

			standardEmplCtrRegistDate: formatDate(dayjs()),
			year: dayjs().year().toString(),
			district: applicantData?.districtName || '',
			managementNumber: '',
			// mealsType: [MEAL_TYPES[1]] as string[],
		};
	},
	title: '표준근로 계약서',
	pages: [
		{
			background: {
				src: Images.standardLaborContract1,
				className: 'template',
				dataSource: Images.standardLaborContract1,
			},
			textAreas: [
				{
					name: 'year',
					maxHeight: 28,
					maxLength: 4,
					containerClassName: 'absolute top-[67px] left-[148px] w-[60px]',
					className: '!text-center text-[18px] !h-[22px]',
					regex: REGEX.ALLOW_NUMBER,
					placeholder: '',
				},
				(data, index) => ({
					name: 'district',
					maxHeight: 40,
					maxLength: 8,
					containerClassName: cn(
						'absolute top-[57px] left-[255px] w-[47px] items-end'
					),
					className: '!text-center text-[12px] leading-[22px]',
					regex: REGEX.ALLOW_KR,
				}),
				{
					name: 'managementNumber',
					maxHeight: 28,
					maxLength: 11,
					containerClassName: 'absolute top-[68px] left-[347px] w-[130px]',
					className: '!text-left text-[18px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				(data, index) => ({
					name: 'enterpriseName',
					maxHeight: 48,
					maxLength: 20,
					containerClassName: cn(
						'absolute top-[206px] left-[150px] w-[275px]',
						data?.enterpriseName && 'bg-white'
					),
				}),
				{
					name: 'identificationNumber',
					maxHeight: 28,
					maxLength: 20,
					containerClassName: 'absolute top-[223px] left-[432px] w-[257px]',
					className: 'leading-4',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'enterpriseLocation',
					maxHeight: 20,
					maxLength: 50,
					containerClassName: 'absolute top-[270px] left-[150px] w-[543px]',
				},
				{
					name: 'phoneNumber',
					maxHeight: 20,
					maxLength: 30,
					containerClassName: 'absolute top-[304px] left-[433px] w-[244px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},

				(data, index) => ({
					name: 'applicantName',
					maxHeight: 13,
					maxLength: 20,
					containerClassName: cn('absolute top-[343px] left-[150px] w-[198px]'),
				}),
				(data, index) => ({
					name: 'address',
					maxHeight: 18,
					maxLength: 50,
					containerClassName: cn('absolute top-[375px] left-[149px] w-[545px]'),
				}),
				(data, index) => ({
					name: 'reEnteringMonth',
					maxHeight: 13,
					maxLength: 2,
					containerClassName: cn('absolute top-[420px] left-[585px] w-[18px]'),
					regex: REGEX.ALLOW_NUMBER,
				}),
				(data, index) => ({
					name: 'changeWorkplaceMonth',
					maxHeight: 13,
					maxLength: 2,
					containerClassName: cn('absolute top-[454px] left-[585px] w-[18px]'),
					regex: REGEX.ALLOW_NUMBER,
				}),
				(data, index) => ({
					name: 'employAddress',
					maxHeight: 29,
					maxLength: 100,
					containerClassName: cn('absolute top-[474px] left-[150px] w-[543px]'),
					className: 'leading-none',
				}),
				{
					name: 'typeOfBusiness',
					maxHeight: 35,
					maxLength: 17,
					containerClassName: 'absolute top-[713px] left-[258px] w-[181px]',
				},
				(data, index) => ({
					name: 'businessDescr',
					maxHeight: 35,
					maxLength: 17,
					containerClassName: cn(
						'absolute top-[713px] left-[528px] w-[167px]',
						data.businessDescr && 'bg-white'
					),
				}),
				(data, index) => ({
					name: 'jobDescr',
					maxHeight: 26,
					maxLength: 35,
					containerClassName: cn('absolute top-[751px] left-[259px] w-[436px]'),
				}),
				{
					name: 'workingDayFrom',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: 'absolute top-[888px] left-[242px] w-[26px]',
					className: '!text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'workingDayTo',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: 'absolute top-[888px] left-[321px] w-[26px]',
					className: '!text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'workingDayFrom',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: 'absolute top-[903px] left-[403px] w-[62px]',
					className: '!text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'workingDayTo',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: 'absolute top-[903px] left-[479px] w-[62px]',
					className: '!text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},

				{
					name: 'workingHourPerMonth',
					maxHeight: 20,
					maxLength: 5,
					containerClassName: 'absolute top-[915px] left-[529px] w-[44px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'workingHourPerMonth',
					maxHeight: 20,
					maxLength: 5,
					containerClassName: 'absolute top-[932px] left-[536px] w-[44px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'workingHourWeather',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[985px] left-[478px] w-[34px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'workingHourWeather',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[1000px] left-[387px] w-[18px]',
					className: 'text-[10px] !text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
			],
			datePickers: [
				{
					name: 'employerBirthday',
					className: 'w-[110px] px-1 [&>.ant-picker-input>input]:!text-sm',
					format: 'YYYY-MM-DD',
					containerClassName: 'absolute top-[303px] left-[150px]',
				},
				{
					name: 'birthDate',
					className: 'w-[110px] px-1 [&>.ant-picker-input>input]:!text-sm',
					format: 'YYYY-MM-DD',
					containerClassName: 'absolute top-[339px] left-[432px]',
				},
				(data, index) => ({
					name: 'reEnteringStartDate',
					segmentedInputs: true,
					containerClassName: 'absolute top-[427px] left-[150px] w-[200px]',
					yearClassName: 'absolute left-0 w-[30px]',
					monthClassName: 'absolute left-[70px] w-[20px]',
					dayClassName: 'absolute left-[137px] w-[30px]',

					disabledDate: (current) =>
						disabledDateStartDate(current, data.reEnteringEndDate),
				}),
				(data, index) => ({
					name: 'reEnteringEndDate',
					segmentedInputs: true,
					containerClassName: 'absolute top-[427px] left-[369px] w-[207px]',
					yearClassName: 'absolute left-0 w-[31px]',
					monthClassName: 'absolute left-[72px] w-[20px]',
					dayClassName: 'absolute left-[138px] w-[20px]',

					disabledDate: (current) =>
						disabledDateEndDate(current, data.reEnteringStartDate),
				}),
				(data, index) => ({
					name: 'changeWorkplaceStartDate',
					segmentedInputs: true,
					containerClassName: 'absolute top-[462px] left-[150px] w-[200px]',
					yearClassName: 'absolute left-0 w-[30px]',
					monthClassName: 'absolute left-[70px] w-[20px]',
					dayClassName: 'absolute left-[137px] w-[30px]',

					disabledDate: (current) =>
						disabledDateStartDate(current, data.changeWorkplaceEndDate),
				}),
				(data, index) => ({
					name: 'changeWorkplaceEndDate',
					segmentedInputs: true,
					containerClassName: 'absolute top-[462px] left-[369px] w-[207px]',
					yearClassName: 'absolute left-0 w-[31px]',
					monthClassName: 'absolute left-[72px] w-[20px]',
					dayClassName: 'absolute left-[138px] w-[20px]',

					disabledDate: (current) =>
						disabledDateEndDate(current, data.changeWorkplaceStartDate),
				}),

				// (data, index) => ({
				// 	name: 'entryDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[12px]',
				// 	format: 'YYYY        MM       DD',
				// 	containerClassName: 'absolute top-[322px] left-[150px]',

				// 	disabledDate: (current) =>
				// 		disabledDateStartDate(current, data.changeWorkplaceEndDate),
				// 	style: {
				// 		fontSize: '12px',
				// 		marginTop: '-6px',
				// 	},
				// }),
				// (data, index) => ({
				// 	name: 'departureDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[12px]',
				// 	format: 'YYYY       MM        DD',
				// 	containerClassName: 'absolute top-[322px] left-[390px]',

				// 	disabledDate: (current) =>
				// 		disabledDateEndDate(current, data.changeWorkplaceStartDate),
				// 	style: {
				// 		fontSize: '12px',
				// 		marginTop: '-6px',
				// 	},
				// }),

				// (data, index) => ({
				// 	name: 'entryDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[12px]',
				// 	format: 'YY/MM/DD',
				// 	containerClassName: 'absolute top-[376px] left-[240px]',

				// 	disabledDate: (current) =>
				// 		disabledDateStartDate(current, data.changeWorkplaceEndDate),
				// 	style: {
				// 		fontSize: '12px',
				// 	},
				// }),
				// (data, index) => ({
				// 	name: 'departureDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[12px]',
				// 	format: 'YY/MM/DD',
				// 	containerClassName: 'absolute top-[376px] left-[395px]',

				// 	disabledDate: (current) =>
				// 		disabledDateEndDate(current, data.changeWorkplaceStartDate),
				// 	style: {
				// 		fontSize: '12px',
				// 	},
				// }),

				// (data, index) => ({
				// 	name: 'changeWorkplaceStartDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[10px]',
				// 	format: 'YY/MM/DD',
				// 	containerClassName: 'absolute top-[391px] left-[438px]',
				// 	disabledDate: (current) =>
				// 		disabledDateStartDate(current, data.changeWorkplaceEndDate),
				// 	style: {
				// 		fontSize: '10px',
				// 	},
				// }),
				// (data, index) => ({
				// 	name: 'changeWorkplaceEndDate',
				// 	className: 'w-[130px] [&>.ant-picker-input>input]:!text-[10px]',
				// 	format: 'YY/MM/DD',
				// 	containerClassName: 'absolute top-[391px] left-[570px]',
				// 	disabledDate: (current) =>
				// 		disabledDateEndDate(current, data.changeWorkplaceStartDate),
				// 	style: {
				// 		fontSize: '10px',
				// 	},
				// }),
			],
			timePickers: [
				(data) => ({
					name: 'workingHourFrom',
					className: 'w-[70px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH          mm',
					placeholder: '',
					containerClassName: 'absolute top-[916px] left-[257px]',
					disabledTime: () => disabledRangeTimeFrom(data.workingHourTo),
					style: {
						fontSize: '12px',
					},
				}),
				(data) => ({
					name: 'workingHourTo',
					className: 'w-[70px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH        mm',
					placeholder: '',
					containerClassName: 'absolute top-[916px] left-[366px]',
					disabledTime: () => disabledRangeTimeTo(data.workingHourFrom),
					style: {
						fontSize: '12px',
					},
				}),

				(data) => ({
					name: 'workingHourFrom',
					className: 'w-[37px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH:mm',
					placeholder: '',
					containerClassName: 'absolute top-[932px] left-[356px]',
					disabledTime: () => disabledRangeTimeFrom(data.workingHourTo),
					style: {
						fontSize: '12px',
					},
				}),
				(data) => ({
					name: 'workingHourTo',
					className: 'w-[35px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH:mm',
					placeholder: '',
					containerClassName: 'absolute top-[932px] left-[452px]',
					disabledTime: () => disabledRangeTimeTo(data.workingHourFrom),
					style: {
						fontSize: '12px',
					},
				}),
			],

			checkboxes: [
				{
					name: 'agricultureType',
					iconType: 'circle',
					options: AGRICULTURE_TYPE,
					classNames: [
						'absolute top-[118px] left-[369px] w-[14px] h-[14px]',
						'absolute top-[118px] left-[475px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'agricultureType',
					iconType: 'circle',
					options: AGRICULTURE_TYPE,
					classNames: [
						'absolute top-[136px] left-[432px] w-[14px] h-[14px]',
						'absolute top-[136px] left-[553px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'laborContract',
					iconType: 'circle',
					options: LABOR_CONTRACT,
					type: CheckboxType.MULTIPLE,
					classNames: [
						'absolute top-[401px] left-[157px] w-[14px] h-[14px]',
						'absolute top-[436px] left-[157px] w-[14px] h-[14px]',
					],
					// Reset chi cac field thuoc dung dong cua option vua bam,
					// khong dung chung 1 danh sach cho ca 2 checkbox
					getResetKey: (value: string) => {
						if (value === LABOR_CONTRACT[1]) {
							return [
								'changeWorkplaceStartDate',
								'changeWorkplaceEndDate',
								'changeWorkplaceMonth',
							];
						}
						return ['reEnteringStartDate', 'reEnteringEndDate', 'reEnteringMonth'];
					},
				},
			],
			highlightBoxes: [
				{
					name: 'year',
					className: 'top-[73px] left-[148px] !h-[20px] w-[60px]',
				},
				{
					name: 'district',
					className: 'top-[75px] left-[255px] !h-[20px] w-[47px]',
				},
				{
					name: 'managementNumber',
					className: 'top-[73px] left-[347px] !h-[20px] w-[135px]',
				},
				{
					name: 'agricultureType',
					className: 'top-[114px] left-[362px] !h-[20px] w-[26px]',
				},
				{
					name: 'agricultureType',
					className: 'top-[114px] left-[470px] !h-[20px] w-[26px]',
				},
				{
					name: 'agricultureType',
					className: 'top-[134px] left-[426px] !h-[19px] w-[23px]',
				},
				{
					name: 'agricultureType',
					className: 'top-[134px] left-[550px] !h-[19px] w-[23px]',
				},
				(values, index) => ({
					names: [
						'enterpriseName',
						'phoneNumber',
						'enterpriseLocation',
						'employerBirthday',
						'identificationNumber',
					],
					className: 'top-[239px] left-[76px] !h-[16px] w-[50px]',
					hasError: values?.identificationNumber?.length < 13,
				}),
				{
					name: 'enterpriseName',
					className: 'top-[192px] left-[150px] !h-[16px] w-[178px]',
				},
				{
					name: 'identificationNumber',
					className: 'top-[192px] left-[432px] !h-[16px] w-[178px]',
				},
				{
					name: 'enterpriseLocation',
					className: 'top-[256px] left-[150px] !h-[16px] w-[178px]',
				},
				{
					name: 'employerBirthday',
					className: 'top-[291px] left-[150px] !h-[16px] w-[188px]',
				},
				{
					name: 'phoneNumber',
					className: 'top-[291px] left-[432px] !h-[16px] w-[178px]',
				},
				{
					names: ['applicantName', 'birthDate', 'address'],
					className: 'top-[343px] left-[77px] !h-[16px] w-[50px]',
				},
				{
					name: 'applicantName',
					className: 'top-[325px] left-[150px] !h-[16px] w-[170px]',
				},
				{
					name: 'birthDate',
					className: 'top-[325px] left-[432px] !h-[16px] w-[148px]',
				},
				{
					name: 'address',
					className: 'top-[361px] left-[150px] !h-[16px] w-[199px]',
				},
				{
					names: [
						'newOrReenter',
						'changeWorkplaceStartDate',
						'changeWorkplaceEndDate',
					],
					// `newOrReenter` co gia tri mac dinh nen neu dung `every` thi box
					// khong bao gio do duoc, du 2 ngay con trong.
					requireAllNames: true,
					className: 'top-[400px] left-[47px] !h-[16px] w-[94px]',
				},
				{
					name: 'newOrReenter',
					className: 'top-[400px] left-[152px] !h-[16px] w-[25px]',
				},
				{
					name: 'newOrReenter',
					className: 'top-[435px] left-[152px] !h-[16px] w-[25px]',
				},
				(values, index) => ({
					names: [
						'reEnteringStartDate',
						'reEnteringEndDate',
						'reEnteringMonth'
					],
					// Ca nhom phai dien du, thieu mot field la to do.
					requireAllNames: true,
					className: cn(
						'top-[418px] left-[150px] !h-[16px] w-[460px]',
						!values?.laborContract?.includes(LABOR_CONTRACT[0]) && 'invisible'
					),
				}),
				(values, index) => ({
					names: [
						'changeWorkplaceStartDate',
						'changeWorkplaceEndDate',
						'changeWorkplaceMonth'
					],
					// Ca nhom phai dien du, thieu mot field la to do.
					requireAllNames: true,
					className: cn(
						'top-[453px] left-[150px] !h-[16px] w-[460px]',
						!values?.laborContract?.includes(LABOR_CONTRACT[1]) && 'invisible'
					),
				}),
				{
					name: 'employAddress',
					className: 'top-[474px] left-[150px] !h-[29px] w-[543px]',
				},
				{
					names: ['typeOfBusiness', 'businessDescr', 'jobDescr'],
					className: 'top-[753px] left-[46px] !h-[27px] w-[96px]',
				},
				(values, index) => ({
					name: 'typeOfBusiness',
					className: cn('top-[715px] left-[182px] !h-[16px] w-[36px]'),
				}),
				(values, index) => ({
					name: 'businessDescr',
					className: cn('top-[711px] left-[452px] !h-[16px] w-[51px]', values?.businessDescr?.length === 0 && '!bg-orange-1'),
				}),
				(values, index) => ({
					name: 'jobDescr',
					className: cn('top-[749px] left-[173px] !h-[16px] w-[56px]'),
				}),
				{
					names: [
						'workingHourFrom',
						'workingHourTo',
						'workingHourPerMonth',
						'workingHourWeather',
					],
					className: 'top-[929px] left-[46px] !h-[22px] w-[96px]',
				},
				{
					name: 'workingHourFrom',
					className: 'top-[889px] left-[242px] !h-[16px] w-[130px]',
				},
				{
					name: 'workingHourTo',
					className: 'top-[902px] left-[377px] !h-[16px] w-[180px]',
				},
				{
					name: 'workingHourPerMonth',
					className: 'top-[917px] left-[242px] !h-[16px] w-[337px]',
				},
				{
					name: 'workingHourPerMonth',
					className: 'top-[933px] left-[242px] !h-[16px] w-[350px]',
				},
				{
					name: 'workingHourWeather',
					className: 'top-[987px] left-[471px] !h-[16px] w-[50px]',
				},
				{
					name: 'workingHourWeather',
					className: 'top-[1001px] left-[390px] !h-[16px] w-[13px]',
				},
			],
		},
		{
			background: {
				src: Images.standardLaborContract2,
				className: 'template',
				dataSource: Images.standardLaborContract2,
			},
			textAreas: [
				(data, index) => ({
					name: 'recessHoursTimes',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: cn('absolute top-[118px] left-[185px] w-[34px]'),
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				}),
				(data, index) => ({
					name: 'recessHoursTotalHours',
					maxHeight: 18,
					maxLength: 2,
					containerClassName: cn('absolute top-[118px] left-[261px] w-[34px]'),
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				}),
				{
					name: 'recessHoursTotalMinutes',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[118px] left-[343px] w-[29px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},

				{
					name: 'recessHoursTimes',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[135px] left-[154px] w-[27px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'recessHoursTotalHours',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[135px] left-[337px] w-[27px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'recessHoursTotalMinutes',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[135px] left-[431px] w-[27px]',
					className: '!text-center',
					regex: REGEX.ALLOW_NUMBER,
				},
				(data, index) => ({
					name: 'holidaysEtc',
					maxHeight: 20,
					maxLength: 18,
					containerClassName: cn(
						'absolute invisible top-[204px] left-[318px] w-[166px]',
						data.holidays === HOLIDAYS[2] && 'visible'
					),
				}),

				(data, index) => ({
					name: 'wage',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'absolute invisible top-[269px] left-[238px] w-[95px]',
						data.timePerWage && 'visible'
					),
					regex: REGEX.ALLOW_NUMBER,
					currency: true,
				}),
				(data, index) => ({
					name: 'bonusPay',
					maxHeight: 20,
					maxLength: 6,
					containerClassName: cn('absolute top-[345px] left-[327px] w-[43px]'),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
					disabled: data.bonusOrExtraPay !== BONUS_OR_EXTRA_PAGE[0],
				}),
				(data, index) => ({
					name: 'extraPay',
					maxHeight: 20,
					maxLength: 6,
					containerClassName: cn('absolute top-[345px] left-[492px] w-[48px]'),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
					disabled: data.bonusOrExtraPay !== BONUS_OR_EXTRA_PAGE[0],
				}),
				(data, index) => ({
					name: 'overTimePay',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'invisible absolute top-[363px] left-[509px] w-[64px]',
						data.bonusOrExtraPay === BONUS_OR_EXTRA_PAGE[0] && 'visible'
					),
					className: 'text-[12px] !text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),
				(data, index) => ({
					name: 'overTimePay',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'invisible absolute top-[380px] left-[492px] w-[64px]',
						data.bonusOrExtraPay === BONUS_OR_EXTRA_PAGE[0] && 'visible'
					),
					className: 'text-[12px] !text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),

				// (data, index) => ({
				// 	name: 'paymentDatePerMonth',
				// 	maxHeight: 20,
				// 	maxLength: 2,
				// 	containerClassName: cn(
				// 		'absolute top-[463px] left-[613px] !w-[22px]',
				// 		data?.paymentDatePerMonth && 'bg-white'
				// 	),
				// 	className: '!text-center',
				// 	regex: REGEX.ALLOW_NUMBER,
				// }),
				{
					name: 'paymentDatePerWeek',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[490px] left-[349px] !w-[40px]',
					className: '!text-center',
				},
				(data, index) => ({
					name: 'calculateFrom',
					maxHeight: 14,
					maxLength: 3,
					containerClassName: cn(
						'absolute top-[477px] left-[154px] !w-[33px]'
						// data?.calculateFrom && 'bg-white'
					),
					className: '!text-center',
				}),
				{
					name: 'calculateFrom',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[525px] left-[235px] !w-[27px]',
					className: '!text-center',
				},
				(data, index) => ({
					name: 'calculateTo',
					maxHeight: 14,
					maxLength: 3,
					containerClassName: cn(
						'absolute top-[477px] left-[249px] !w-[26px]'
						// data?.calculateTo && 'bg-white'
					),
					className: '!text-center',
				}),
				{
					name: 'calculateTo',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[525px] left-[292px] !w-[27px]',
					className: '!text-center',
				},
				(data, index) => ({
					name: 'paymentDatePerMonth',
					maxHeight: 14,
					maxLength: 2,
					containerClassName: cn(
						'absolute top-[477px] left-[594px] !w-[21px]'
						// data?.paymentDatePerMonth && 'bg-white'
					),
					className: '!text-center',
				}),
				{
					name: 'paymentDatePerMonth',
					maxHeight: 20,
					maxLength: 2,
					containerClassName: 'absolute top-[525px] left-[398px] !w-[16px]',
					className: '!text-center',
				},
				{
					name: 'paymentDatePerWeek',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: 'absolute top-[541px] left-[154px] !w-[60px]',
					className: '!text-center',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				// {
				// 	name: 'paymentDatePerWeek',
				// 	maxHeight: 20,
				// 	maxLength: 3,
				// 	containerClassName: 'absolute top-[541px] left-[473px] !w-[50px]',
				// 	className: '!text-center',
				// },
				(data, index) => ({
					name: 'provideAccommondationOther',
					maxHeight: 20,
					maxLength: 24,
					containerClassName: cn(
						'absolute top-[690px] left-[451px] !w-[240px]'
					),
					className: '!text-left',
					disabled: data.provideAccommondationStatus === '1',
				}),
				(data, index) => ({
					name: 'otherLodging',
					maxHeight: 20,
					maxLength: 20,
					containerClassName: cn(
						'absolute top-[760px] left-[477px] !w-[196px]'
					),
					className: 'whitespace-pre',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled:
						// data.provideAccommondationStatus !== '1' &&
						!data.provideAccommondationType?.includes(ACCOMMODATION_TYPES[4]),
				}),
				(data, index) => ({
					name: 'accommodationCost',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'absolute invisible top-[777px] left-[363px] !w-[71px]',
						data.provideAccommondationStatus === '1' && 'visible'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),
				(data, index) => ({
					name: 'accommodationCost',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'absolute top-[795px] left-[507px] !w-[55px] invisible',
						data.provideAccommondationStatus === '1' && 'visible'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),
				(data, index) => ({
					name: 'mealsProviderOther',
					maxHeight: 20,
					maxLength: 24,
					containerClassName: cn(
						'absolute top-[832px] left-[451px] !w-[233px]'
					),
					className: '!text-left',
					disabled: data.provideMeals === '1',
				}),

				(data, index) => ({
					name: 'mealsCost',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'absolute top-[850px] left-[363px] !w-[73px] invisible',
						data?.provideMeals === '1' && 'visible'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),
				(data, index) => ({
					name: 'mealsCost',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: cn(
						'absolute top-[868px] left-[478px] !w-[60px] invisible',
						data?.provideMeals === '1' && 'visible'
					),
					className: 'text-[10px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),
			],

			checkboxes: [
				{
					name: 'holidays',
					iconType: 'circle',
					options: HOLIDAYS,
					classNames: [
						'absolute top-[190px] left-[157px] w-[14px] h-[14px]',
						'absolute top-[190px] left-[322px] w-[14px] h-[14px]',
						'absolute top-[207px] left-[157px] w-[14px] h-[14px]',
					],
					getResetKey: (value: string) => {
						let result: string[] = [];

						if (value === HOLIDAYS[2]) {
							result = ['holidaysEtc'];
						}

						return result;
					},
				},
				{
					name: 'timePerWage',
					iconType: 'circle',
					options: TIME_PER_WAGE,
					classNames: [
						'absolute top-[288px] left-[177px]  w-[14px] h-[14px]',
						'absolute top-[288px] left-[300px]  w-[14px] h-[14px]',
						'absolute top-[288px] left-[431px]  w-[14px] h-[14px]',
						'absolute top-[288px] left-[542px]  w-[14px] h-[14px]',
					],
					getResetKey: (value: string) => {
						return ['wage'];
					},
				},
				{
					name: 'weeklyHolidayAllow',
					iconType: 'circle',
					options: BONUS_OR_EXTRA_PAGE,
					classNames: [
						'absolute top-[306px] left-[468px] w-[14px] h-[14px]',
						'absolute top-[306px] left-[568px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'bonusOrExtraPay',
					iconType: 'circle',
					options: BONUS_OR_EXTRA_PAGE,
					classNames: [
						'absolute top-[348px] left-[175px] w-[14px] h-[14px]',
						'absolute top-[348px] left-[595px] w-[14px] h-[14px]',
					],
					getResetKey: (value: string) => {
						return ['extraPay', 'overTimePay', 'bonusPay'];
					},
				},
				{
					name: 'paymentDate',
					iconType: 'circle',
					options: PAYMENT_DATE,
					classNames: [
						'absolute top-[477px] left-[402px] w-[14px] h-[14px]',
						'absolute top-[477px] left-[490px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'paymentWeek',
					iconType: 'circle',
					options: PAYMENT_WEEK,
					classNames: [
						'absolute top-[494px] left-[157px] w-[14px] h-[14px]',
						'absolute top-[494px] left-[245px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'paymentDate',
					iconType: 'circle',
					options: PAYMENT_DATE,
					classNames: [
						'absolute top-[528px] left-[478px] w-[14px] h-[14px]',
						'absolute top-[528px] left-[575px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'paymentWeek',
					iconType: 'circle',
					options: PAYMENT_WEEK,
					classNames: [
						'absolute top-[545px] left-[272px] w-[14px] h-[14px]',
						'absolute top-[545px] left-[375px] w-[14px] h-[14px]',
					],
				},
				{
					name: 'provideAccommondationStatus',
					iconType: 'circle',
					options: PROVIDED_STATUS,
					classNames: [
						'absolute top-[693px] left-[177px] w-[14px] h-[14px]',
						'absolute top-[693px] left-[302px] w-[14px] h-[14px]',
					],
					getResetKey: (value: string) => {
						return [
							'provideAccommondationType',
							'otherLodging',
							'provideAccommondationStatusNot',
							'provideAccommondationOther',
							'accommodationCost',
						];
					},
				},
				(data, index) => ({
					type: CheckboxType.SINGLE,
					name: 'provideAccommondationType',
					iconType: 'circle',
					classNames: [
						'absolute top-[712px] left-[514px] w-[14px] h-[14px]',
						'absolute top-[712px] left-[609px] w-[14px] h-[14px]',
						'absolute top-[746px] left-[182px] w-[14px] h-[14px]',
						'absolute top-[746px] left-[409px] w-[14px] h-[14px]',
						'absolute top-[764px] left-[184px] w-[14px] h-[14px]',
					],
					options: ACCOMMODATION_TYPES,
					disabled: data.provideAccommondationStatus === '0',
					// getResetKey: (value: string) => {
					// 	return ['otherLodging'];
					// },
				}),
				{
					name: 'provideMeals',
					iconType: 'circle',
					options: PROVIDED_STATUS,
					classNames: [
						'absolute top-[817px] left-[363px] w-[14px] h-[14px]',
						'absolute top-[834px] left-[299px] w-[14px] h-[14px]',
					],
					getResetKey: (value: string) => {
						if (value === '0') {
							return ['mealsType'];
						}
						return ['mealsCost', 'mealsProviderOther'];
					},
				},
				(data, index) => ({
					type: CheckboxType.MULTIPLE,
					name: 'mealsType',
					iconType: 'circle',
					classNames: [
						'absolute top-[817px] left-[480px] w-[14px] h-[14px]',
						cn(
							'absolute top-[817px] left-[608px] w-3 h-3 invisible',
							data?.provideMeals === '1' && 'visible'
						),
						'absolute top-[834px] left-[181px] w-[14px] h-[14px]',
					],
					options: MEAL_TYPES,
					getResetKey: (value: string) => {
						return ['provideMeals', 'mealsProviderOther'];
					},
				}),
			],

			highlightBoxes: [
				{
					names: [
						'recessHoursTimes',
						'recessHoursTotalHours',
						'recessHoursTotalMinutes',
					],
					className: 'top-[125px] left-[56px] !h-[20px] w-[86px]',
				},
				{
					name: 'recessHoursTimes',
					className: 'top-[120px] left-[186px] !h-[14px] w-[34px]',
				},
				{
					name: 'recessHoursTotalHours',
					className: 'top-[120px] left-[261px] !h-[14px] w-[34px]',
				},
				{
					name: 'recessHoursTotalMinutes',
					className: 'top-[120px] left-[343px] !h-[14px] w-[29px]',
				},
				(data) => {
					return {
						names: ['holidays', 'holidaysEtc'],
						className: 'top-[209px] left-[56px] !h-[20px] w-[70px]',
					};
				},
				{
					names: ['timePerWage', 'bonusOrExtraPay', 'extraPay'],
					className: 'top-[354px] left-[48px] !h-[20px] w-[91px]',
				},
				{
					names: ['paymentDatePerMonth', 'paymentDatePerWeek'],
					className: 'top-[513px] left-[48px] !h-[20px] w-[91px]',
				},
				(data) => ({
					names: [
						'provideAccommondationStatus',
						'provideAccommondationType',
						'accommodationCost',
						'provideMeals',
						'mealsType',
						'mealsCost',
					],

					className: 'top-[805px] left-[48px] !h-[20px] w-[91px]',
				}),

				{
					name: 'recessHoursTimes',
					className: 'top-[135px] left-[154px] !h-[20px] w-[27px]',
				},
				{
					name: 'recessHoursTotalHours',
					className: 'top-[135px] left-[337px] !h-[20px] w-[27px]',
				},
				{
					name: 'recessHoursTotalMinutes',
					className: 'top-[135px] left-[431px] !h-[20px] w-[27px]',
				},
				(data) => ({
					name: 'holidaysEtc',
					className: cn(
						'top-[204px] left-[318px] !h-[20px] w-[166px]',
						data.holidays !== HOLIDAYS[2] && data.holidaysEtc?.length === 0 && 'invisible'
					),
				}),
				(data) => ({
					name: 'wage',
					className: cn(
						'top-[269px] left-[238px] !h-[20px] w-[95px]'
						// data.timePerWage && 'visible'
					),
				}),
				(data) => ({
					name: 'bonusPay',
					className: cn(
						'top-[345px] left-[327px] !h-[20px] w-[43px]',
						data.bonusOrExtraPay === BONUS_OR_EXTRA_PAGE[1] && 'invisible'
					),
				}),
				(data) => ({
					name: 'extraPay',
					className: cn(
						'top-[345px] left-[492px] !h-[20px] w-[48px]',
						data.bonusOrExtraPay === BONUS_OR_EXTRA_PAGE[1] && 'invisible'
					),
				}),
				{
					name: 'overTimePay',
					className: 'top-[363px] left-[509px] !h-[20px] w-[64px]',
				},
				{
					name: 'overTimePay',
					className: 'top-[380px] left-[492px] !h-[20px] w-[64px]',
				},
				(data) => ({
					name: 'paymentDatePerWeek',
					className: cn('top-[490px] left-[349px] !h-[20px] w-[40px] invisible', data.paymentWeek === PAYMENT_WEEK[1] && 'visible'),
				}),
				{
					name: 'calculateFrom',
					className: 'top-[525px] left-[235px] !h-[20px] w-[27px]',
				},
				{
					name: 'calculateTo',
					className: 'top-[525px] left-[292px] !h-[20px] w-[27px]',
				},
				{
					name: 'paymentDatePerMonth',
					className: 'top-[525px] left-[398px] !h-[20px] w-[16px]',
				},
				{
					name: 'paymentDatePerMonth',
					className: 'top-[541px] left-[154px] !h-[20px] w-[60px]',
				},
				// {
				// 	name: 'paymentDatePerWeek',
				// 	className: 'top-[541px] left-[473px] !h-[20px] w-[50px]',
				// },
				(data) => ({
					name: 'provideAccommondationOther',
					className: cn(
						'top-[690px] left-[451px] !h-[20px] w-[240px]',
						data.provideAccommondationStatus === '1' && 'invisible'
					),
				}),
				(data) => ({
					name: 'otherLodging',
					className: cn(
						'top-[760px] left-[477px] !h-[20px] w-[196px]',
						!data.provideAccommondationType?.includes(ACCOMMODATION_TYPES[4]) && 'invisible'
					),
				}),
				(data) => ({
					name: 'accommodationCost',
					className: cn(
						'top-[777px] left-[363px] !h-[20px] w-[71px]',
						data.provideAccommondationStatus === '1' && 'invisible'
					),
				}),
				(data) => ({
					name: 'accommodationCost',
					className: cn(
						'top-[795px] left-[507px] !h-[20px] w-[55px]',
						data.provideAccommondationStatus === '1' && 'invisible'
					),
				}),
				(data) => ({
					name: 'mealsProviderOther',
					className: cn(
						'top-[832px] left-[451px] !h-[20px] w-[233px]',
						data.provideMeals === '1' && 'invisible'
					),
				}),
				(data) => ({
					name: 'mealsCost',
					className: cn(
						' top-[850px] left-[363px] !h-[20px] w-[73px]',
						data?.provideMeals === '1' && 'invisible'
					),
				}),
				(data) => ({
					name: 'mealsCost',
					className: cn(
						'top-[868px] left-[478px] !h-[20px] w-[60px]',
						data?.provideMeals === '1' && 'invisible'
					),
				}),

				{
					name: 'holidays',
					className: 'top-[190px] left-[152px] !h-[14px] w-[25px]',
				},
				{
					name: 'holidays',
					className: 'top-[190px] left-[316px] !h-[14px] w-[25px]',
				},
				{
					name: 'holidays',
					className: 'top-[207px] left-[152px] !h-[14px] w-[25px]',
				},
				{
					name: 'timePerWage',
					className: 'top-[288px] left-[171px] !h-[14px] w-[25px]',
				},
				{
					name: 'timePerWage',
					className: 'top-[288px] left-[295px] !h-[14px] w-[25px]',
				},
				{
					name: 'timePerWage',
					className: 'top-[288px] left-[426px] !h-[14px] w-[25px]',
				},
				{
					name: 'timePerWage',
					className: 'top-[288px] left-[537px] !h-[14px] w-[25px]',
				},
				{
					name: 'weeklyHolidayAllow',
					className: 'top-[306px] left-[462px] !h-[14px] w-[25px]',
				},
				{
					name: 'weeklyHolidayAllow',
					className: 'top-[306px] left-[562px] !h-[14px] w-[25px]',
				},
				{
					name: 'bonusOrExtraPay',
					className: 'top-[348px] left-[172px] !h-[14px] w-[20px]',
				},
				{
					name: 'bonusOrExtraPay',
					className: 'top-[348px] left-[593px] !h-[14px] w-[20px]',
				},
				{
					name: 'calculateFrom',
					className: 'top-[477px] left-[154px] !h-[14px] w-[33px]',
				},
				{
					name: 'calculateTo',
					className: 'top-[477px] left-[249px] !h-[14px] w-[27px]',
				},
				{
					name: 'paymentDatePerMonth',
					className: 'top-[477px] left-[594px] !h-[14px] w-[21px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[477px] left-[399px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[477px] left-[487px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[494px] left-[155px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[494px] left-[243px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[528px] left-[476px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[528px] left-[572px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[545px] left-[269px] !h-[14px] w-[20px]',
				},
				{
					name: 'paymentDate',
					className: 'top-[545px] left-[372px] !h-[14px] w-[20px]',
				},
				{
					name: 'provideAccommondationStatus',
					className: 'top-[693px] left-[174px] !h-[14px] w-[20px]',
				},
				{
					name: 'provideAccommondationStatus',
					className: 'top-[693px] left-[300px] !h-[14px] w-[20px]',
				},
				(data) => ({
					name: 'provideAccommondationType',
					className: cn(
						'top-[712px] left-[511px] !h-[14px] w-[20px]',
						data.provideAccommondationStatus === '0' && 'invisible'
					),
				}),
				(data) => ({
					name: 'provideAccommondationType',
					className: cn(
						'top-[712px] left-[607px] !h-[14px] w-[20px]',
						data.provideAccommondationStatus === '0' && 'invisible'
					),
				}),
				(data) => ({
					name: 'provideAccommondationType',
					className: cn(
						'top-[746px] left-[180px] !h-[14px] w-[20px]',
						data.provideAccommondationStatus === '0' && 'invisible'
					),
				}),
				(data) => ({
					name: 'provideAccommondationType',
					className: cn(
						'top-[746px] left-[406px] !h-[14px] w-[20px]',
						data.provideAccommondationStatus === '0' && 'invisible'
					),
				}),
				(data) => ({
					name: 'provideAccommondationType',
					className: cn(
						'top-[764px] left-[181px] !h-[14px] w-[20px]',
						data.provideAccommondationStatus === '0' && 'invisible'
					),
				}),
				{
					name: 'provideMeals',
					className: 'top-[817px] left-[360px] !h-[14px] w-[20px]',
				},
				{
					name: 'provideMeals',
					className: 'top-[834px] left-[297px] !h-[14px] w-[20px]',
				},
				{
					name: 'mealsType',
					className: 'top-[817px] left-[477px] !h-[14px] w-[20px]',
				},
				{
					name: 'mealsType',
					className: 'top-[817px] left-[605px] !h-[14px] w-[20px]',
				},
				{
					name: 'mealsType',
					className: 'top-[834px] left-[178px] !h-[14px] w-[20px]',
				},
			],
		},
		{
			background: {
				src: Images.standardLaborContract3,
				className: 'template w-full px-[9px]',
				dataSource: Images.standardLaborContract3,
			},
			textAreas: [
				{
					name: 'standardEmployer',
					maxHeight: 40,
					containerClassName: 'absolute w-[193px] top-[517px] left-[331px]',
					className: 'leading-4',
				},
				{
					name: 'standardEmplCtrEmployee',
					maxHeight: 40,
					containerClassName: 'absolute w-[193px] top-[557px] left-[327px]',
					className: 'leading-4',
				},
			],
			datePickers: [
				{
					name: 'standardEmplCtrRegistDate',
					segmentedInputs: true,
					format: 'YYYY                      MM                             DD',
					containerClassName: 'absolute top-[484px] left-[389px]',
					style: { fontSize: '14px' },
				},
			],
			signatures: [
				{
					name: 'standardEmployerSignature',
					className: 'absolute w-[139px] h-[40px] top-[524px] left-[523px]',
				},

				{
					name: 'standardEmplCtrSignature',
					className: 'absolute w-[139px] h-[40px] top-[564px] left-[523px]',
				},
			],
			highlightBoxes: [
				{
					name: 'standardEmplCtrRegistDate',
					className: 'top-[484px] left-[389px] !h-[20px] w-[319px]',
				},
				{
					name: 'standardEmployer',
					className: 'top-[524px] left-[212px] !h-[20px] w-[113px]',
				},
				{
					name: 'standardEmplCtrEmployee',
					className: 'top-[564px] left-[212px] !h-[20px] w-[113px]',
				},
				{
					name: 'standardEmployerSignature',
					className: 'top-[524px] left-[523px] !h-[20px] w-[185px]',
				},
				{
					name: 'standardEmplCtrSignature',
					className: 'top-[564px] left-[523px] !h-[20px] w-[185px]',
				},
			],
		},
	],
	validationSchema: Yup.object()
		.shape({
			employAddress: Yup.string().required(),
			wage: Yup.string().required(),
			paymentMethod: Yup.string().required(),
			standardEmplCtrRegistDate: Yup.string().required(),
			standardEmployer: Yup.string().required(),
			standardEmplCtrEmployee: Yup.string().required(),
			identificationNumber: Yup.string().min(13),
		})
		.test(
			'at-least-one-field',
			'At least one field must have data',
			function (value) {
				const fields = [
					[
						'enterpriseName',
						'phoneNumber',
						'enterpriseLocation',
						'employerBirthday',
						// 'identificationNumber',
					],
					['applicantName', 'birthDate', 'address'],
					[
						'newOrReenter',
						'changeWorkplaceStartDate',
						'changeWorkplaceEndDate',
					],
					['typeOfBusiness', 'jobDescr'],
					[
						'workingHourFrom',
						'workingHourTo',
						'workingHourPerMonth',
						'workingHourWeather',
					],
					[
						'recessHoursTimes',
						'recessHoursTotalHours',
						'recessHoursTotalMinutes',
					],
					['holidays', 'holidaysEtc'],
					['timePerWage', 'bonusOrExtraPay', 'extraPay'],
					['paymentDatePerMonth', 'paymentDatePerWeek'],
					[
						'provideAccommondationStatus',
						'provideAccommondationType',
						'accommodationCost',
						'provideMeals',
						'mealsType',
						'mealsCost',
					],
				];
				return fields.every((field) =>
					// @ts-expect-error: ignore here
					field?.some((f) => isNotNilOrEmpty(value[f]))
				);
			}
		),
};
